import { Resize } from '@ifit/img/out/resize';

export const resize = new Resize();

export const getResizedImgUrl = (...args) => resize.getUrl(...args);
export const isResizable = url => resize.isResizableUrl(url);

export const minPixelRatio = 0.5;
export const defaultPixelRatio = 2.0;
export const maxPixelRatio = 2.5;
export const resizeMax = 3000;
const standardDimensions = [
  90,
  128,
  180,
  204,
  212,
  256,
  290,
  310,
  408,
  426,
  480,
  580,
  640,
  768,
  900,
  1024,
  1280,
  1440,
  1536,
  1600,
  1728,
  1800,
  1920,
  2048,
  2100,
  2200,
  2300,
  2400,
  2560,
  2700,
  2800,
  2900,
  resizeMax,
]
  .sort((a, b) => a - b)
  .filter(d => d <= resizeMax);

function calculateDimension(dimension, pixelRatio) {
  if (!dimension) return null;
  return Math.floor(dimension * pixelRatio);
}

function between(lower, target, upper) {
  return Math.min(Math.max(lower, target), upper);
}

export function getStandardDimension(dimension, useStandardDimensions) {
  if (!dimension) return null;
  if (!useStandardDimensions) return dimension;
  for (let i = 0; i < standardDimensions.length; i++) {
    const currDimension = standardDimensions[i];
    if (currDimension >= dimension) {
      return currDimension;
    }
  }
  return undefined;
}

export function imageResizer(
  url,
  dimensions = {
    pixelRatio: minPixelRatio,
  },
  resizeOptions = {
    isPlaceholder: false,
    position: '',
    useStandardDimensions: true,
  }
) {
  return getResizedImageDetails(
    {
      ...dimensions,
      pixelRatio: resizeOptions.isPlaceholder
        ? minPixelRatio
        : dimensions.pixelRatio,
    },
    resizeOptions,
    url
  ).image;
}

export function getResizedImageDetails(dimensions, resizeOptions, image) {
  const defaultDetails = {
    width: dimensions.width,
    height: dimensions.height,
    image,
    wasResized: false,
  };

  if (!isResizable(image)) return defaultDetails;

  try {
    if (!dimensions.pixelRatio) {
      dimensions.pixelRatio = defaultPixelRatio;
    }
    const widthExceeded = dimensions.width * dimensions.pixelRatio > resizeMax;
    const heightExceeded = dimensions.height * dimensions.pixelRatio > resizeMax;
    if (widthExceeded || heightExceeded) {
      dimensions.pixelRatio = widthExceeded
        ? resizeMax / dimensions.width
        : resizeMax / dimensions.height;
    }
    const { width, height } = scaleDimensions({
      ...dimensions,
      ...resizeOptions,
    });

    const resizedImage = getResizedImgUrl(image, {
      width,
      height,
      xOffsetDirection: resizeOptions.position
    });

    return { width, height, image: resizedImage, wasResized: true };
  } catch (error) {
    error.message = error.message + ' Defaulting to regular, unsized url.';
    console.warn(error); // eslint-disable-line no-console
    return defaultDetails;
  }
}

export function scaleDimensions({
  height,
  width,
  pixelRatio = minPixelRatio,
  ignoreMinPixelRatio,
  useStandardDimensions = true,
}) {
  const resizePixelRatio = ignoreMinPixelRatio
    ? pixelRatio
    : between(minPixelRatio, pixelRatio, maxPixelRatio);

  const requestedWidth = calculateDimension(width, resizePixelRatio);
  if (requestedWidth > resizeMax)
    throw new Error(
      `Calculated width (${requestedWidth}) exceeds max of ${resizeMax}.`
    );

  const requestedHeight = calculateDimension(height, resizePixelRatio);
  if (requestedHeight > resizeMax)
    throw new Error(
      `Calculated height (${requestedHeight}) exceeds max of ${resizeMax}.`
    );

  const resizeWidth = getStandardDimension(
    requestedWidth,
    useStandardDimensions
  );
  const resizeHeight = getStandardDimension(
    requestedHeight,
    useStandardDimensions
  );

  if (!resizeWidth && !resizeHeight)
    throw new Error('Could not determine a width or height.');

  return { height: resizeHeight, width: resizeWidth };
}
